import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import { PlayArrow, Pause, Delete, History, Edit, Repeat } from '@mui/icons-material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject } from '@syncfusion/ej2-react-grids';
import { RecurringTaskView, useGetRecurringTasksQuery, usePauseRecurringTaskMutation, useResumeRecurringTaskMutation, useDeleteRecurringTaskMutation } from '../../../../services/RecurringTaskService';
import { LoadingIndicator, SectionPaper } from '../../../common/components/styled-ui-elements';
import { CreateRecurringTaskButton } from '../RecurringTaskForm/actions';

interface RecurringTasksGridProps {
  searchText?: string;
  showInactive?: boolean;
}

export const RecurringTasksGrid: React.FC<RecurringTasksGridProps> = ({ 
  searchText = '',
  showInactive = false
}) => {
  const navigate = useNavigate();
  const { data: recurringTasks, isLoading, refetch } = useGetRecurringTasksQuery();
  const [pauseTask] = usePauseRecurringTaskMutation();
  const [resumeTask] = useResumeRecurringTaskMutation();
  const [deleteTask] = useDeleteRecurringTaskMutation();

  const handlePauseResume = async (task: RecurringTaskView) => {
    try {
      if (task.isActive) {
        await pauseTask(task.id).unwrap();
      } else {
        await resumeTask(task.id).unwrap();
      }
      refetch();
    } catch (error) {
      console.error('Failed to pause/resume task:', error);
    }
  };

  const handleDelete = async (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this recurring task? This will not affect already generated tickets.')) {
      try {
        await deleteTask(taskId).unwrap();
        refetch();
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  // Filter tasks based on search text and active status
  const filteredTasks = React.useMemo(() => {
    if (!recurringTasks) return [];
    
    let filtered = recurringTasks;

    // Filter by active status
    if (!showInactive) {
      filtered = filtered.filter(task => task.isActive);
    }

    // Apply search text filter
    if (searchText.trim() !== '') {
      filtered = filtered.filter(task =>
        task.templateTitle.toLowerCase().includes(searchText.toLowerCase()) ||
        task.category.toLowerCase().includes(searchText.toLowerCase()) ||
        task.priority.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return filtered;
  }, [recurringTasks, searchText, showInactive]);

  const getRecurrenceTypeLabel = (type: string): string => {
    switch (type) {
      case 'DAILY': return 'Daily';
      case 'WEEKLY': return 'Weekly';
      case 'MONTHLY': return 'Monthly';
      case 'CUSTOM': return 'Custom';
      default: return type;
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'CRIT': return '#dc2626';
      case 'HIGH': return '#ea580c';
      case 'MED': return '#0891b2';
      case 'LOW': return '#059669';
      default: return '#64748b';
    }
  };

  const actionTemplate = (task: RecurringTaskView) => {
    return (
      <Box display="flex" gap={0.5}>
        <IconButton 
          size="small" 
          onClick={() => navigate(`/recurring-tasks/edit?id=${task.id}`)}
          title="Edit"
          sx={{ color: '#3b82f6' }}
        >
          <Edit fontSize="small" />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={() => handlePauseResume(task)}
          title={task.isActive ? 'Pause' : 'Resume'}
          sx={{ color: task.isActive ? '#f59e0b' : '#10b981' }}
        >
          {task.isActive ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
        </IconButton>
        <IconButton 
          size="small" 
          onClick={() => navigate(`/recurring-tasks/history?id=${task.id}`)}
          title="View History"
          sx={{ color: '#8b5cf6' }}
        >
          <History fontSize="small" />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={() => handleDelete(task.id)}
          title="Delete"
          sx={{ color: '#ef4444' }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const statusTemplate = (task: RecurringTaskView) => {
    return (
      <Chip 
        label={task.isActive ? 'Active' : 'Paused'} 
        size="small"
        sx={{
          backgroundColor: task.isActive ? '#10b981' : '#ef4444',
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: '24px',
          '& .MuiChip-label': {
            px: 1.5,
            py: 0.5
          }
        }}
      />
    );
  };

  const priorityTemplate = (task: RecurringTaskView) => {
    const priorityColor = getPriorityColor(task.priority);
    return (
      <Chip 
        label={task.priority} 
        size="small"
        sx={{
          backgroundColor: priorityColor,
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: '24px',
          '& .MuiChip-label': {
            px: 1.5,
            py: 0.5
          }
        }}
      />
    );
  };

  const scheduleTemplate = (task: RecurringTaskView) => {
    return (
      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
        {getRecurrenceTypeLabel(task.schedule.type)} 
        {task.schedule.interval > 1 && ` (every ${task.schedule.interval})`}
      </Typography>
    );
  };

  if (isLoading) {
    return (
      <SectionPaper>
        <LoadingIndicator icon={Repeat} message="Loading recurring tasks..." />
      </SectionPaper>
    );
  }

  if (!filteredTasks || filteredTasks.length === 0) {
    return (
      <SectionPaper>
        <Box textAlign="center" py={2}>
          <Typography variant="body2" color="textSecondary">
            No recurring tasks found. Create your first recurring task to automate ticket generation.
          </Typography>
          <Box mt={1.5}>
            <CreateRecurringTaskButton />
          </Box>
        </Box>
      </SectionPaper>
    );
  }

  return (
    <SectionPaper>
      <Typography 
        variant="caption" 
        sx={{ 
          mb: 1, 
          display: 'block',
          color: '#64748b',
          fontWeight: 500
        }}
      >
        Showing {filteredTasks.length} recurring task{filteredTasks.length !== 1 ? 's' : ''}
      </Typography>
      <GridComponent
        dataSource={filteredTasks}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 20 }}
        sortSettings={{
          columns: [{ field: 'createdDate', direction: 'Descending' as any }]
        }}
        height={500}
        rowHeight={36}
        gridLines="Both"
      >
        <ColumnsDirective>
          <ColumnDirective 
            field="templateTitle" 
            headerText="Title" 
            width="180"
            textAlign="Left"
          />
          <ColumnDirective 
            field="category" 
            headerText="Category" 
            width="100"
            textAlign="Left"
          />
          <ColumnDirective 
            headerText="Priority" 
            width="90"
            textAlign="Center"
            template={priorityTemplate}
          />
          <ColumnDirective 
            headerText="Schedule" 
            width="110"
            textAlign="Left"
            template={scheduleTemplate}
          />
          <ColumnDirective 
            headerText="Status" 
            width="85"
            textAlign="Center"
            template={statusTemplate}
          />
          <ColumnDirective 
            field="generatedTicketCount" 
            headerText="Tickets" 
            width="70"
            textAlign="Center"
          />
          <ColumnDirective 
            field="nextScheduledDate" 
            headerText="Next Run" 
            width="100"
            textAlign="Left"
            format="yMd"
            type="date"
          />
          <ColumnDirective 
            headerText="Actions" 
            width="140"
            textAlign="Center"
            template={actionTemplate}
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </SectionPaper>
  );
};

export default RecurringTasksGrid;
